// --- React & Redux
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Row } from 'antd';
import Select from '@dtci/spellbook/Select';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { getSearchInputVisibility } from '../utils/validations';
// --- Hooks & Store
import { useRightsActions } from 'datastage/store/rights/useRights';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import '../../styles.scss';

/**
 * Input Search Component
 *
 * Features:
 * - Renders search inputs for Deal and Catalog based on visibility flags
 * - Handles asynchronous search with debouncing
 * - Updates Redux state on selection
 *
 * @param details
 * @returns {Element}
 * @constructor
 */
export function InputSearch({ details }) {
    const timeoutRef = useRef();
    const { associationForm } = useSelector((state) => state.rights);
    const { updateRightsForm } = useRightsActions();
    const { shouldShowDealSearch, shouldShowCatalogSearch } = getSearchInputVisibility(details);

    const DEBOUNCE_TIMEOUT = 500;

    // Handle deal search input changes with debouncing
    const onDealOptionChange = (input, loadOptions) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(async () => {
            if (input !== '') {
                const data = await datastageApi.deals(input);
                const mappedOptions = data.map(item => ({
                    id: item?.id,
                    label: item?.searchableTitle
                }));
                loadOptions(mappedOptions);
            }
        }, DEBOUNCE_TIMEOUT);
    };

    // Handle catalog search input changes with debouncing
    const onCatalogOptionChange = (input, loadOptions) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(async () => {
            if (input !== '') {
                const data = await datastageApi.catalogs(input);
                const mappedOptions = data.map(item => ({
                    id: item?.id,
                    label: item?.searchableTitle
                }));
                loadOptions(mappedOptions);
            }
        }, DEBOUNCE_TIMEOUT);
    };

    const handleInputChange = (value, field) => {
        updateRightsForm({
            key: 'associationForm',
            data: { [field]: value },
        });
    };

    return (
        <>
            { shouldShowDealSearch && (
                    <Row>
                        <InputLabel colProps={{ span: 24 }} label="Deal">
                            <Select
                                async
                                className="search-input"
                                loadOptions={onDealOptionChange}
                                isClearable
                                isSearchable
                                onChange={value => handleInputChange(value, 'dealSelected')}
                                value={associationForm?.dealSearchInput}
                            />
                        </InputLabel>
                    </Row>
                )}

            { shouldShowCatalogSearch && (
                    <Row>
                        <InputLabel colProps={{ span: 24 }} label="Catalog">
                            <Select
                                async
                                className="search-input"
                                loadOptions={onCatalogOptionChange}
                                isClearable
                                isSearchable
                                onChange={value => handleInputChange(value, 'catalogSelected')}
                                value={associationForm?.catalogSearchInput}
                            />
                        </InputLabel>
                    </Row>
                )}
        </>
    );
}
